import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '../ui/button';
import { Sheet, SheetContent, SheetTrigger } from '../ui/sheet';
import { Menu, Palette, Sparkles } from 'lucide-react';

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { href: '/', label: 'Home' },
    { href: '/questionnaire', label: 'Get Started' },
    { href: '/dashboard', label: 'Dashboard' },
    { href: '/progress', label: 'Progress' },
  ];

  const isGetStartedContext =
    location.pathname === '/questionnaire' ||
    location.pathname.startsWith('/dashboard/onboarding');

  const showOnlyHomeAndGetStarted =
    location.pathname === '/' || isGetStartedContext || location.pathname.startsWith('/verify');
  const visibleNavLinks = showOnlyHomeAndGetStarted ? navLinks.slice(0, 2) : navLinks;

  const isActive = (path) => {
    if (path === '/questionnaire' && isGetStartedContext) {
      return true;
    }
    return location.pathname === path;
  };

  return (
    <nav className="glass-dark fixed top-0 left-0 right-0 z-50 border-b border-white/5" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 group" data-testid="navbar-logo">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[hsl(234,89%,63%)] to-[hsl(262,83%,58%)] flex items-center justify-center shadow-lg group-hover:shadow-[hsl(234,89%,63%)]/30 transition-shadow">
              <Palette className="w-4.5 h-4.5 text-white" />
            </div>
            <span className="text-lg font-bold text-white font-['Outfit']">
              Skill<span className="text-[hsl(262,83%,58%)]">Skulpt</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="desktop-nav items-center gap-1">
            {visibleNavLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                data-testid={`nav-link-${link.label.toLowerCase().replace(' ', '-')}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-150 ${
                  isActive(link.href)
                    ? 'bg-white/10 text-white'
                    : 'text-slate-300 hover:text-white hover:bg-white/5'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Desktop CTA */}
          <div className="desktop-nav items-center gap-3">
            <Link to="/questionnaire" data-testid="navbar-cta">
              <Button className="bg-[hsl(234,89%,63%)] hover:bg-[hsl(234,89%,58%)] text-white rounded-full px-5 h-9 shadow-md hover:shadow-lg transition-all text-sm font-medium">
                <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                Start Free
              </Button>
            </Link>
          </div>

          {/* Mobile Menu */}
          <div className="mobile-nav">
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="text-white" data-testid="mobile-menu-trigger">
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-[hsl(222,47%,11%)] border-l border-white/5 w-[280px]">
                <div className="flex flex-col h-full pt-8">
                  <div className="flex items-center gap-2.5 mb-8 px-2">
                    <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[hsl(234,89%,63%)] to-[hsl(262,83%,58%)] flex items-center justify-center">
                      <Palette className="w-4.5 h-4.5 text-white" />
                    </div>
                    <span className="text-lg font-bold text-white font-['Outfit']">
                      Skill<span className="text-[hsl(262,83%,58%)]">Skulpt</span>
                    </span>
                  </div>
                  
                  <div className="flex flex-col gap-1">
                    {visibleNavLinks.map((link) => (
                      <Link
                        key={link.href}
                        to={link.href}
                        onClick={() => setIsOpen(false)}
                        data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(' ', '-')}`}
                        className={`px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                          isActive(link.href)
                            ? 'bg-[hsl(234,89%,63%)]/15 text-white border-l-2 border-[hsl(234,89%,63%)]'
                            : 'text-slate-300 hover:text-white hover:bg-white/5'
                        }`}
                      >
                        {link.label}
                      </Link>
                    ))}
                  </div>

                  <div className="mt-auto pb-8">
                    <Link to="/questionnaire" onClick={() => setIsOpen(false)}>
                      <Button className="w-full bg-[hsl(234,89%,63%)] hover:bg-[hsl(234,89%,58%)] text-white rounded-full h-11 font-medium" data-testid="mobile-cta">
                        <Sparkles className="w-4 h-4 mr-2" />
                        Start Your Journey
                      </Button>
                    </Link>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
